<template>
  <div>
    <div class="flex flex-col">
      <div class="w-full bg-action-bar px-2 py-2 clearfix">
        <div class="float-right flex flex-wrap">
          <div class="mx-2 mt-2 lg:mt-0">
            <router-link class="btn-green" to="/user/new">
              {{ $t("create_user") }}
              <BaseIcon icon="plus" class="ml-2" />
            </router-link>
          </div>
        </div>
      </div>
      <div class="w-full mt-6">
        <vue-good-table
          mode="remote"
          styleClass="vgt-table"
          row-style-class="text-sm"
          :columns="tableColumn"
          :rows="users"
          :totalRows="totalRecords"
          :search-options="{
            enabled: true,
            placeholder: $t('create_user_page.search'),
          }"
          :pagination-options="{
            enabled: true,
            perPage: 100,
            perPageDropdown: [100],
            dropdownAllowAll: true,
          }"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
          @on-sort-change="onSortChange"
          @on-search="onSearch"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'name'"> {{ props.row.firstname }} {{ props.row.lastname }} </span>
            <span v-else-if="props.column.field == 'role'">
              <span v-if="props.row.role == 'user'">{{ $t("user_role.department") }}</span>
              <span v-if="props.row.role == 'superuser'">{{ $t("user_role.customer") }}</span>
              <span v-if="props.row.role == 'admin'">{{ $t("user_role.admin") }}</span>
              <span v-if="props.row.role == 'alarm_center'">{{ $t("user_role.alarm_center") }}</span>
              <span v-if="props.row.role == 'tech'">{{ $t("user_role.tech") }}</span>
              <span v-if="props.row.role == 'reseller'">{{ $t("user_role.reseller") }}</span>
              <span v-if="props.row.role == 'seller'">{{ $t("user_role.seller") }}</span>
              <span v-if="props.row.role == 'securdesktop'">SecurDesktop</span>
              <span v-if="props.row.role == 'securone_basic' || props.row.role == 'basic_user'">SecurOne</span>
            </span>
            <span v-else-if="props.column.field == 'securOne'" v-show="user.role == 'admin'">
              <span v-if="props.row.securOne && props.row.securOne.status == 'success'" class="px-2 py-1 rounded text-xs font-bold text-white bg-green-500">
                OK
              </span>
              <span v-else-if="props.row.securOne && props.row.securOne.status == 'pending'" class="px-2 py-1 rounded text-xs font-bold text-white bg-yellow-500">
                Pending
              </span>
              <span v-else-if="props.row.securOne && props.row.securOne.status == 'failed'" class="px-2 py-1 rounded text-xs font-bold text-white bg-red-500" :title="props.row.securOne.lastError ? props.row.securOne.lastError.message : ''">
                Failed
              </span>
              <span v-else class="px-2 py-1 rounded text-xs font-bold text-gray-500 bg-gray-200">
                -
              </span>
            </span>
            <span v-else-if="props.column.field == 'edit'">
              <button class="btn-secondary-outline" @click.prevent="showUpdate(props.row)">
                {{ $t("edit") }}
                <BaseIcon icon="pencil-alt" class="ml-1" />
              </button>
            </span>
            <span v-else-if="props.column.field == 'delete'">
              <button class="btn-red-outline" @click.prevent="deleteUserPrompt(props.row)">
                {{ $t("delete") }}
                <BaseIcon icon="trash" class="ml-1" />
              </button>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>

    <UserUpdate :userInfo="updateInfo" :roles="roles" :allCustomers="customers" :allUnits="units" :alarm_centers="alarm_centers" :resellers="resellers" :sellers="sellers" :technicians="technicians" @updateUser="getUsers()" />
  </div>
</template>

<script>
import UserUpdate from "@/components/modal/user_update";

export default {
  name: "Users",
  title() {
    return this.$t("page_titles.user");
  },
  components: {
    UserUpdate,
  },
  data() {
    return {
      users: [],
      customers: [],
      units: [],
      alarm_centers: [],
      resellers: [],
      sellers: [],
      technicians: [],
      updateInfo: null,
      modalDataLoaded: false,
      totalRecords: 0,
      serverParams: {
        page: 1,
        perPage: 100,
        search: '',
        sortField: '_id',
        sortType: 'desc',
      },
      searchTimer: null,
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
    tableColumn() {
      const columns = [
        { label: this.$t("user_table.name"), field: "name", sortable: false },
        { label: this.$t("user_table.user_name"), field: "username", sortable: false },
        { label: this.$t("user_table.email"), field: "email", sortable: false },
        { label: this.$t("user_table.telephone"), field: "phone", sortable: false },
        { label: this.$t("user_table.role"), field: "role", sortable: true },
      ];
      
      // Only show SecurOne column for admin users
      if (this.user.role === 'admin') {
        columns.push({ label: "SecurOne", field: "securOne", sortable: false });
      }
      
      columns.push(
        { label: "", field: "edit", sortable: false },
        { label: "", field: "delete", sortable: false }
      );
      
      return columns;
    },
    roles() {
      return [
        { id: "admin", name: this.$t("user_role.admin") },
        { id: "superuser", name: this.$t("user_role.customer") },
        { id: "user", name: this.$t("user_role.department") },
        { id: "alarm_center", name: this.$t("user_role.alarm_center") },
        { id: "tech", name: this.$t("user_role.tech") },
        { id: "reseller", name: this.$t("user_role.reseller") },
        { id: "seller", name: this.$t("user_role.seller") },
        { id: "securdesktop", name: "SecurDesktop"},
        { id: "basic_user", name: "SecurOne" },
      ];
    },
  },
  methods: {
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },
    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadUsers();
    },
    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage, page: 1 });
      this.loadUsers();
    },
    onSortChange(params) {
      if (!params[0]) return;
      this.updateParams({ sortField: params[0].field, sortType: params[0].type, page: 1 });
      this.loadUsers();
    },
    onSearch(params) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.updateParams({ search: params.searchTerm, page: 1 });
        this.loadUsers();
      }, 400);
    },
    loadUsers() {
      const params = {
        page: this.serverParams.page,
        perPage: this.serverParams.perPage,
        search: this.serverParams.search,
        sortField: this.serverParams.sortField,
        sortType: this.serverParams.sortType,
      };
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/users`, { params })
        .then((response) => {
          this.users = response.data.users.map(user => ({
            ...user,
            name: `${user.firstname} ${user.lastname}`
          }));
          this.totalRecords = response.data.totalRecords;
        })
        .catch((errors) => {
          this.handleError(errors);
        });
    },

    getUsers() {
      this.loadUsers();
    },

    async getCustomers() {
      try {
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/customer/`);
        this.customers = response.data.data;
      } catch (error) {
        this.handleError(error);
      }
    },

    async getUnits() {
      try {
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/units/`);
        this.units = response.data.data;
      } catch (error) {
        this.handleError(error);
      }
    },

    async getAlarmCenters() {
      try {
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`);
        this.alarm_centers = response.data.data;

        if (this.user.role == "reseller") {
          let resellerAlarmCenterId = this.$store.state.user.reseller_alarm_center;
          let resellerAlarmCenter = response.data.data.find(({_id}) => _id === resellerAlarmCenterId);
          this.alarm_centers = resellerAlarmCenter ? [resellerAlarmCenter] : [];
        }
      } catch (error) {
        this.handleError(error);
      }
    },

    async getResellers() {
      try {
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/reseller`);
        this.resellers = response.data.data;
      } catch (error) {
        this.handleError(error);
      }
    },

    async getSellers() {
      try {
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/sellers`);
        this.sellers = response.data.data;
      } catch (error) {
        this.handleError(error);
      }
    },

    async getTechnicians() {
      try {
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/technicians`);
        this.technicians = response.data.data;
      } catch (error) {
        this.handleError(error);
      }
    },

    deleteUserPrompt(user) {
      this.$modal.show("dialog", {
        title: `<span class="text-red-500">${this.$t("delete_user")} ${user.username}?</span>`,
        buttons: [
          {
            title: `<div class="bg-success-500 text-white text-sm font-sans py-2">${this.$t("user_actions.cancel")}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t("delete")}</div>`,
            handler: () => {
              this.deleteUser(user._id);
            },
          },
        ],
      });
    },

    async deleteUser(_id) {
      try {
        // eslint-disable-next-line
        let response = await this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/user/${_id}`);

        this.$modal.hide("dialog");
        this.handleSuccess(this.$t("user_removed_message"));

        this.getUsers();
      } catch (error) {
        this.$modal.hide("dialog");

        let error_message = this.$t("problem_occured");
        if (error.response && error.response.status == 400) error_message = this.$t("insufficient_permissions");
        this.handleError(error, error_message);
      }
    },

    async showUpdate(user) {
      // Load the dropdown option lists (sellers, resellers, …) BEFORE opening
      // the modal. If the modal opens while these are still empty, the
      // BaseSelect values (e.g. seller_id) don't match any option yet, so they
      // show the placeholder — and since BaseSelect binds :value (not a native
      // v-model on <select>), Vue doesn't re-sync the selection once the
      // options arrive. Awaiting here guarantees options exist at first render.
      if (!this.modalDataLoaded) {
        this.modalDataLoaded = true;
        const loads = [this.getCustomers(), this.getUnits()];
        if (this.user.role == "admin") {
          loads.push(this.getAlarmCenters(), this.getResellers(), this.getSellers(), this.getTechnicians());
        }
        if (this.user.role == "reseller") {
          loads.push(this.getAlarmCenters());
        }
        await Promise.all(loads);
      }
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/users/${user._id}`);
        this.updateInfo = _.cloneDeep(response.data.user);
      } catch (error) {
        this.handleError(error);
        return;
      }
      this.$modal.show("update-user");
    },

    hide(id) {
      this.$modal.hide(id);
      this.edit_user = null;
    },
  },

  created() {
    this.loadUsers();

    this.setPageTitle(this.$t("top_titles.user"), "users");
  },
  watch: {
    "$i18n.locale"() {
      document.title = this.$t("page_titles.user");
      this.setPageTitle(this.$t("top_titles.user"), "users");
    },
  },
};
</script>
